import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  BokkzAlgDataService,
  BokkzWageDataService,
  Component1,
  Component2,
  Dienstverband,
  Periode,
  WerkgeverEntity,
  WerknemerEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import {ToastService} from '@snelstart/ui-toast';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {MultiSelectOption, SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {debounceTime, filter, merge, Observable, shareReplay, Subscription, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type ComponentFormGroup = FormGroup<{
  periode: PeriodeFormGroup;
  component: FormControl<Component2 | null>;
  displayValue: FormControl<string | null>;
  eenheid: FormControl<number | null>;
  bedrag: FormControl<number | null>;
  percentage: FormControl<number | null>;
  proRataVergoeding: FormControl<number | null>;
  grondslagComponenten: FormControl<number[]>;
  periodeVrijval: FormControl<number | null>;
  cumulatiefSaldoBeginPeriode: FormControl<number | null>;
  berekendeAanwas: FormControl<number | null>;
  cumulatiefSaldoEindPeriode: FormControl<number | null>;
}>;

type LijstFormGroup = FormGroup<{
  vergoedingen: FormArray<ComponentFormGroup>;
  reserveringen: FormArray<ComponentFormGroup>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-vastevergoeding',
  standalone: true,
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiTabModule,
    SharedUiDialogModule,
    SharedUiIconModule
  ],
  templateUrl: './vastevergoeding.component.html',
  styleUrl: './vastevergoeding.component.scss'
})
export class VastevergoedingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabDv = 0;
  private readonly destroyRef = inject(DestroyRef);
  public readonly algDataService = inject(BokkzAlgDataService);
  public readonly dataService = inject(BokkzWageDataService);
  public readonly toastService = inject(ToastService);

  public werkgeverEntity = {} as WerkgeverEntity;
  public werknemerEntity = {} as WerknemerEntity;

  public lijstFg: LijstFormGroup = new FormGroup({
    vergoedingen: new FormArray<ComponentFormGroup>([]),
    reserveringen: new FormArray<ComponentFormGroup>([])
  });

  public periode = {} as Periode;
  public index = 0;
  public dialogVisible = false;
  public selectedTab = 0;
  private calculationSubscription?: Subscription;

  public detailsConfig = DetailsConfig.create({
    hideArrowButtons: false,
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  public optionsVrijval: SelectFormControlItem<number>[] = [
    {value: 1, label: 'Periode 1'},
    {value: 2, label: 'Periode 2'},
    {value: 3, label: 'Periode 3'},
    {value: 4, label: 'Periode 4'},
    {value: 5, label: 'Periode 5'},
    {value: 6, label: 'Periode 6'},
    {value: 7, label: 'Periode 7'},
    {value: 8, label: 'Periode 8'},
    {value: 9, label: 'Periode 9'},
    {value: 10, label: 'Periode 10'},
    {value: 11, label: 'Periode 11'},
    {value: 12, label: 'Periode 12'},
    {value: 99, label: 'Periodieke vrijval'},
  ];

  public optionsComponenten: SelectFormControlItem<Component2>[] = [
    {value: Component2.SALARIS, label: 'Salaris'},
    {value: Component2.PERSOONLIJKE_TOESLAG, label: 'Persoonlijke toeslag'},
    {value: Component2.BIJDRAGE_AUTO_PRIVE, label: 'Inhouding eigen bijdrage privégebruik auto'},
    {value: Component2.FISCALE_BIJTELLING_AUTO, label: 'Bijtelling privégebruik auto'},
    {value: Component2.FISCALE_BIJTELLING_FIETS, label: 'Bijtelling privégebruik fiets'},
    {value: Component2.REISKOSTEN_WOON_WERK_VAST, label: 'Netto reiskosten woon-werk'},
    {value: Component2.ONKOSTENVERGOEDING_WKR, label: 'Netto onkostenvergoeding WKR'},
    {value: Component2.STAGEVERGOEDING, label: 'Stagevergoeding'},
  ];

  public optionsComponentenReservering: SelectFormControlItem<Component2>[] = [
    {value: Component2.VAKANTIEBIJSLAG, label: 'Vakantiebijslag'},
    {value: Component2.EINDJAARS_TOESLAG, label: 'Eindejaarstoeslag'},
  ];

  public optionsGrondslagComponenten: MultiSelectOption<number, string>[] = [
    {value: 1200, text: 'Salaris'},
    {value: 1201, text: 'Persoonlijke toeslag'},
    {value: 1400, text: 'Bonus'},
  ];

  readonly werkgeverEntity$: Observable<WerkgeverEntity> =
    this.dataService.werkgeverEntity$.pipe(
      filter((data): data is WerkgeverEntity => !!data),
      tap((data) => {
        this.werkgeverEntity = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly werknemerEntity$: Observable<WerknemerEntity> =
    this.dataService.werknemerEntity$.pipe(
      filter((data): data is WerknemerEntity => !!data?.id),
      tap((data) => {
        this.werknemerEntity = data;
        this.syncForms();
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly selectedTab$: Observable<number> =
    this.dataService.selectedTab$.pipe(
      tap((value) => {
        this.selectedTab = value;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.werkgeverEntity$.subscribe();
    this.werknemerEntity$.subscribe();
    this.selectedTab$.subscribe();
    this.bindCalculationHandlers();
  }

  private syncForms(): void {
    if (!this.werknemerEntity?.werknemerGegevens?.dienstverbanden?.at(0)) {
      return;
    }
    this.updateFormVergoedingen(this.werknemerEntity?.werknemerGegevens?.dienstverbanden[0].vasteVergoedingen || []);
    this.updateFormReserveringen(this.werknemerEntity?.werknemerGegevens?.dienstverbanden[0].regelingen || []);
    this.bindCalculationHandlers();
  }

  ngOnDestroy(): void {
    this.calculationSubscription?.unsubscribe();
    this.saveForm();
  }

  public showDialog(index: number): void {
    this.index = index;
    this.dialogVisible = true;
  }

  get vergoedingen(): FormArray<ComponentFormGroup> {
    return this.lijstFg.controls.vergoedingen;
  }

  get reserveringen(): FormArray<ComponentFormGroup> {
    return this.lijstFg.controls.reserveringen;
  }

  addRijVergoedingen(): void {
    this.vergoedingen.push(this.createFormGroup());
    this.bindCalculationHandlers();
  }

  addRijReservering(): void {
    this.reserveringen.push(this.createFormGroup());
    this.bindCalculationHandlers();
  }

  verwijderRij(type: 'vergoeding' | 'reservering', index: number): void {
    const formArray = type === 'vergoeding'
      ? this.vergoedingen
      : this.reserveringen;

    formArray.removeAt(index);
    formArray.updateValueAndValidity();
    formArray.markAsDirty();
    this.bindCalculationHandlers();
    this.saveForm();
    this.dataService
      .calculateLatestWerknemerEntity()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private bindCalculationHandlers(): void {
    this.calculationSubscription?.unsubscribe();

    const vergoedingBedragStreams = this.vergoedingen.controls.map(control =>
      control.controls.bedrag.valueChanges
    );
    const vergoedingEinddatumStreams = this.vergoedingen.controls.map(control =>
      control.controls.periode.controls.einddatum.valueChanges
    );
    const reserveringPercentageStreams = this.reserveringen.controls.map(control =>
      control.controls.periodeVrijval.valueChanges
    );
    const reserveringEinddatumStreams = this.reserveringen.controls.map(control =>
      control.controls.periode.controls.einddatum.valueChanges
    );

    const calculationStreams = [
      ...vergoedingBedragStreams,
      ...vergoedingEinddatumStreams,
      ...reserveringPercentageStreams,
      ...reserveringEinddatumStreams,
    ];
    if (!calculationStreams.length) {
      this.calculationSubscription = undefined;
      return;
    }

    this.calculationSubscription = merge(...calculationStreams)
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveForm();
        this.dataService
          .calculateLatestWerknemerEntity()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
  }

  private einddatumNaBegindatumValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const begin = control.get('begindatum')?.value;
      const eind = control.get('einddatum')?.value;

      if (!begin || !eind) {
        return null;
      }

      const beginDatum = new Date(begin);
      const eindDatum = new Date(eind);

      if (isNaN(beginDatum.getTime()) || isNaN(eindDatum.getTime())) {
        return null;
      }

      return eindDatum < beginDatum
        ? {einddatumVoorBegindatum: true}
        : null;
    };
  }

  createFormGroup(item?: Component1): ComponentFormGroup {
    return new FormGroup({
      periode: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.periode?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.periode?.einddatum ?? null),
      }, {
        validators: this.einddatumNaBegindatumValidator()
      }),
      component: new FormControl<Component2 | null>(item?.component ?? null),
      displayValue: new FormControl<string | null>(item?.displayValue ?? null),
      eenheid: new FormControl<number | null>(item?.eenheid ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      percentage: new FormControl<number | null>(item?.percentage ?? null),
      proRataVergoeding: new FormControl<number | null>(item?.proRataVergoeding ?? null),
      grondslagComponenten: new FormControl<number[]>(item?.grondslagComponenten ?? [], {nonNullable: true}),
      periodeVrijval: new FormControl<number | null>(item?.periodeVrijval ?? null),
      cumulatiefSaldoBeginPeriode: new FormControl<number | null>(item?.cumulatiefSaldoBeginPeriode ?? null),
      berekendeAanwas: new FormControl<number | null>(item?.berekendeAanwas ?? null),
      cumulatiefSaldoEindPeriode: new FormControl<number | null>(item?.cumulatiefSaldoEindPeriode ?? null),
    });
  }

  private hasValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private hasArrayValue(value: unknown): boolean {
    return Array.isArray(value) && value.length > 0;
  }

  private isPeriodeValid(formArray: FormArray<ComponentFormGroup>, index: number): boolean {
    const periodeGroup = formArray.at(index).controls.periode;
    return !!periodeGroup && !periodeGroup.hasError('einddatumVoorBegindatum');
  }

  private hasPeriodeDateOrderErrors(): boolean {
    const vergoedingHasErrors = this.vergoedingen.controls.some(control =>
      control.controls.periode.hasError('einddatumVoorBegindatum')
    );
    const reserveringHasErrors = this.reserveringen.controls.some(control =>
      control.controls.periode.hasError('einddatumVoorBegindatum')
    );

    return vergoedingHasErrors || reserveringHasErrors;
  }

  public hasGeldigeEinddatum(type: 'vergoeding' | 'reservering', index: number): boolean {
    const formArray = type === 'vergoeding' ? this.vergoedingen : this.reserveringen;
    const periodeGroup = formArray.at(index).controls.periode;
    const einddatum = periodeGroup.controls.einddatum.value;

    return !!einddatum && this.isPeriodeValid(formArray, index);
  }

  public isVergoedingReadyForEinddatum(index: number): boolean {
    const row = this.vergoedingen.at(index).getRawValue();

    return this.hasValue(row.component) &&
      this.hasValue(row.bedrag);
  }

  public isReserveringReadyForEinddatum(index: number): boolean {
    const row = this.reserveringen.at(index).getRawValue();

    return this.hasValue(row.component) &&
      this.hasValue(row.percentage) &&
      this.hasArrayValue(row.grondslagComponenten) &&
      this.hasValue(row.periodeVrijval);
  }

  public isVergoedingEinddatumDisabled(index: number): boolean {
    return this.hasGeldigeEinddatum('vergoeding', index) || !this.isVergoedingReadyForEinddatum(index);
  }

  public isReserveringEinddatumDisabled(index: number): boolean {
    return this.hasGeldigeEinddatum('reservering', index) || !this.isReserveringReadyForEinddatum(index);
  }

  public isReserveringMeerGegevensDisabled(index: number): boolean {
    const row = this.reserveringen.at(index).getRawValue();

    const hasAllRequiredValues =
      this.hasValue(row.periode?.begindatum) &&
      this.hasValue(row.component) &&
      this.hasValue(row.percentage);

    return !hasAllRequiredValues || !this.isPeriodeValid(this.reserveringen, index);
  }

  private updateFormVergoedingen(vasteVergoedingen : Component1[]): void {
    const lijst = vasteVergoedingen || [];
    const formArray = this.vergoedingen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(lijst[i]);
        control.updateValueAndValidity({emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup);
      }
    }
  }

  private updateFormReserveringen(reserveringen: Component1[]): void {
    const lijst = reserveringen || [];
    const formArray = this.reserveringen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(lijst[i]);
        control.updateValueAndValidity({emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup);
      }
    }
  }

  public saveForm(): void {
    this.lijstFg.updateValueAndValidity({emitEvent: false});

    // Only block saving when date order is invalid; other transient invalid states should not drop changes.
    if (this.hasPeriodeDateOrderErrors()) {
      return;
    }

    const vasteVergoedingen = this.lijstFg.getRawValue().vergoedingen;
    const regelingen = this.lijstFg.getRawValue().reserveringen;

    this.dataService.patchWerknemerEntity((current) => {
      const baseEntity = current?.werknemerGegevens ? current : this.werknemerEntity;
      const eersteDienstverband = baseEntity.werknemerGegevens?.dienstverbanden?.at(0);

      if (!baseEntity.werknemerGegevens || !eersteDienstverband) {
        return baseEntity;
      }

      const dienstverband = {
        ...eersteDienstverband,
        dga: false,
        vasteVergoedingen,
        regelingen,
      } as Dienstverband;

      const nextWerknemer = {
        ...baseEntity,
        werknemerGegevens: {
          ...baseEntity.werknemerGegevens,
          dienstverbanden: [dienstverband],
        },
      } as WerknemerEntity;

      this.werknemerEntity = nextWerknemer;
      return nextWerknemer;
    });
  }
}
